/** Formats an amount in Swiss francs, e.g. `1234.5` → `CHF 1'234,50`. */
export function formatChf(amount: number): string {
  return formatCurrency(amount, 'CHF');
}

/** Formats an amount with its ISO currency code using the billing display convention. */
export function formatCurrency(amount: number, currency: string): string {
  const [integer, fraction] = amount.toFixed(2).split('.');
  return `${currency} ${groupThousands(integer)},${fraction}`;
}

function groupThousands(value: string): string {
  const sign = value.startsWith('-') ? '-' : '';
  const digits = sign ? value.slice(1) : value;
  const groups: string[] = [];

  for (let end = digits.length; end > 0; end -= 3) {
    groups.unshift(digits.slice(Math.max(0, end - 3), end));
  }
  return `${sign}${groups.join("'")}`;
}

/** Formats a percentage, e.g. `5` → `5,0 %`. */
function formatPercent(value: number): string {
  return `${value.toFixed(1).replace('.', ',')} %`;
}

export interface GapParts {
  /** Signed CHF amount, e.g. `+CHF 12,50`; `CHF 0,00` when within tolerance. */
  amountText: string;
  /** Signed percentage, e.g. `+5,0 %`; `null` when it rounds to zero. */
  percentText: string | null;
  /** Sign of the gap: positive (above), negative (below) or flat. */
  direction: 'positive' | 'negative' | 'flat';
}

/**
 * Splits a signed gap into its display parts. The percentage is omitted
 * (`null`) when it rounds to `0,0 %`, including the within-tolerance case.
 */
export function formatGapParts(amount: number, pct: number): GapParts {
  if (Math.abs(amount) < 0.01) {
    return { amountText: formatChf(0), percentText: null, direction: 'flat' };
  }
  const sign = amount > 0 ? '+' : '-';
  const showPct = Number(pct.toFixed(1)) !== 0;
  return {
    amountText: `${sign}${formatChf(Math.abs(amount))}`,
    percentText: showPct ? `${sign}${formatPercent(Math.abs(pct))}` : null,
    direction: amount > 0 ? 'positive' : 'negative',
  };
}

/** Whether two amounts are equal within a one-cent tolerance. */
export function amountsMatch(amount: number, other: number | null): boolean {
  return other != null && Math.abs(amount - other) < 0.01;
}
